/**
 * Error class raised by the CORTEX API client.
 *
 * Two flavours of code are possible on `code`:
 *
 *   1. An `ErrorCode` from `@cortex/domain/schemas` — the server parsed,
 *      classified, and returned a normal CORTEX error envelope (4xx/5xx
 *      with the documented body). The client preserves the server's
 *      `requestId` so support can correlate the report.
 *
 *   2. A client-only code (`network_error`, `malformed_response`) —
 *      something went wrong _between_ the caller and the server: DNS
 *      failure, TLS error, AbortSignal, body that doesn't parse, body
 *      that doesn't match the schema. These never round-trip a server
 *      `requestId`; callers should surface a generic "could not reach
 *      CORTEX" message instead of leaking the underlying transport
 *      error to the user.
 */

import type { ErrorCode } from '../schemas/error.js';

export type ClientErrorCode = ErrorCode | 'network_error' | 'malformed_response';

export interface ApiClientErrorOptions {
  readonly cause?: unknown;
  readonly body?: unknown;
}

export class ApiClientError extends Error {
  override readonly name = 'ApiClientError';

  readonly code: ClientErrorCode;
  readonly status: number;
  readonly requestId: string | null;
  readonly body: unknown;

  constructor(
    code: ClientErrorCode,
    status: number,
    requestId: string | null,
    options: ApiClientErrorOptions = {},
  ) {
    super(`[${code}] HTTP ${status}${requestId ? ` (requestId=${requestId})` : ''}`);
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.body = options.body;
    if (options.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

export const isApiClientError = (value: unknown): value is ApiClientError =>
  value instanceof ApiClientError;
